import { quoteType } from "../quote-form/quote-type.const";
import { Quote } from "../quote.type";
import { QuoteEmailMapper } from "./email.mapper";
import { QuoteClientEmailDetailsMapper } from "./client-email-details.mapper";
import { FclTariff } from "./quote-fcl-tariff.model";
import { BrokerageQuote } from "./quote-brokerage.model";
import { FclQuote } from "./quote-fcl.model";
import { ManualQuote } from "./quote-manual.model";
import { QuoteLocationMapper } from "./quote-location.mapper";
import { LclQuote } from "./quote-lcl.model";
import { LocationMapper } from "../../location/location.mapper";
import { ClientMapper } from "../../client/client.mapper";
import { RateMapper } from "../../rate/rate.mapper";
import { TierMapper } from "../../tier/tier.mapper";
import { FclTier } from "../../tier/tier-fcl.model";
import { PackageMapper } from "../quote-form/quote-package/package.mapper";
import { AirQuote } from "./quote-air.model";
import { LtlQuote } from "./quote-ltl.model";
import { QuoteSurchargeTypeMapper } from "./quote-surcharge-type.mapper";
import { BaseUserMapper } from "../../user/base-user.mapper";
import { LocalCostLabelMapper } from "./local-cost-label.mapper";
import { FtlQuote } from "./quote-ftl.model";
import { PtlQuote } from "./quote-ptl.model";
import { EmailQuote } from "./quote-email.model";
import { EquipmentQuantity } from "./equipment-quantity.model";
import { AirTariffQuote } from "./quote-airtariff.model";
import { MultiQuote } from "./quote-multi.model";

export class QuoteMapper {
  public static map(data: any): Quote {
    const quote = QuoteMapper.mapQuote(data);

    if (quote) {
      quote.setClientEmailDetails(
        QuoteClientEmailDetailsMapper.map(data.clientEmailDetails),
      );
    }

    return quote;
  }

  private static mapQuote(data: any): Quote {
    if (!data) {
      return null;
    }

    if (data.quoteType) {
      data.discriminator = data.quoteType;
    }

    if (!data.discriminator) {
      console.trace("no discriminator");
    }

    let validFrom = null;
    if (data.validFrom) {
      const date = new Date(data.validFrom);
      validFrom = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          date.getHours(),
          date.getMinutes(),
          date.getSeconds(),
        ),
      );
    }

    let validTo = null;
    if (data.validTo) {
      const date = new Date(data.validTo);
      validTo = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          date.getHours(),
          date.getMinutes(),
          date.getSeconds(),
        ),
      );
    }

    if (
      data.discriminator === quoteType.fcl ||
      data.discriminator === quoteType.brokerage
    ) {
      const QuoteClass =
        data.discriminator === quoteType.brokerage ? BrokerageQuote : FclQuote;
      const fclQuote = new QuoteClass(
        data.id || 0,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.direction,
        data.incotermType,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.hasOrigin,
        data.hasDestination,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.localCostLabels
          ? data.localCostLabels.map(LocalCostLabelMapper.map)
          : [],
        data.equipment ? data.equipment.map((eq) => String(eq)) : [],
        data.equipmentQuantity && Array.isArray(data.equipmentQuantity)
          ? data.equipmentQuantity.reduce((equipmentQuantity, o) => {
              return equipmentQuantity.set(
                o.name,
                new EquipmentQuantity(o.name, o.value),
              );
            }, new Map<string, EquipmentQuantity>())
          : null,
        data.showGrandTotal,
        data.grandTotalCurrency,
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.comment,
        data.showRateSurcharges,
        data.showCarrier,
        data.showServiceNameForLocalCharges,
        data.showRateName,
        data.showRateValidity,
        data.showInland,
        data.showTotal,
        data.totalCurrency,
        data.showInvoiceInfo,
        data.invoiceCurrency
          ? data.invoiceCurrency.map((eq) => String(eq))
          : [],
        data.groupRoutes,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        data.groupOriginSurcharges,
        data.groupDestinationSurcharges,
        data.groupOriginSurchargesCurrency,
        data.groupDestinationSurchargesCurrency,
        data.freightRateOrder,
      );
      fclQuote.setShowChargesInTotalCurrency(!!data.showChargesInTotalCurrency);
      fclQuote.setChargesCurrency(data.chargesCurrency ?? null);

      return fclQuote;
    }

    if (data.discriminator === quoteType.lcl) {
      const lclQuote = new LclQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.direction,
        data.incotermType,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.hasOrigin,
        data.hasDestination,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.localCostLabels
          ? data.localCostLabels.map(LocalCostLabelMapper.map)
          : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.weight,
        data.measurement,
        data.pieces || 0,
        data.comment,
        data.showRateSurcharges,
        data.showCarrier,
        data.showInland,
        data.showTotal,
        data.totalCurrency,
        data.showInvoiceInfo,
        data.invoiceCurrency
          ? data.invoiceCurrency.map((eq) => String(eq))
          : [],
        data.groupRoutes,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.averageMarginPercent,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        data.groupOriginSurcharges,
        data.groupDestinationSurcharges,
        data.groupOriginSurchargesCurrency,
        data.groupDestinationSurchargesCurrency,
        Array.isArray(data.packages)
          ? data.packages.map(PackageMapper.map)
          : [],
        data.chargeableWeight || 0,
        data.ldm,
        data.showRateValidity,
      );
      lclQuote.setShowChargesInTotalCurrency(!!data.showChargesInTotalCurrency);
      lclQuote.setChargesCurrency(data.chargesCurrency ?? null);

      return lclQuote;
    }

    if (data.discriminator === quoteType.air) {
      const airQuote = new AirQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.direction,
        data.incotermType,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.hasOrigin,
        data.hasDestination,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.localCostLabels
          ? data.localCostLabels.map(LocalCostLabelMapper.map)
          : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.weight,
        data.measurement,
        data.pieces || 0,
        data.comment,
        data.showRateSurcharges,
        data.showCarrier,
        data.showInland,
        data.showTotal,
        data.totalCurrency,
        data.showInvoiceInfo,
        data.invoiceCurrency
          ? data.invoiceCurrency.map((eq) => String(eq))
          : [],
        data.groupRoutes,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.averageMarginPercent,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        data.groupOriginSurcharges,
        data.groupDestinationSurcharges,
        data.groupOriginSurchargesCurrency,
        data.groupDestinationSurchargesCurrency,
        Array.isArray(data.packages)
          ? data.packages.map(PackageMapper.map)
          : [],
        data.chargeableWeight || 0,
        data.ldm,
        data.serviceCodes ? data.serviceCodes.map((eq) => String(eq)) : [],
      );
      airQuote.setShowChargesInTotalCurrency(!!data.showChargesInTotalCurrency);
      airQuote.setChargesCurrency(data.chargesCurrency ?? null);

      return airQuote;
    }

    if (data.discriminator === quoteType.multi) {
      return new MultiQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.direction,
        data.incotermType,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.hasOrigin,
        data.hasDestination,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.localCostLabels
          ? data.localCostLabels.map(LocalCostLabelMapper.map)
          : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.weight,
        data.measurement,
        data.pieces || 0,
        data.comment,
        data.showRateSurcharges,
        data.showCarrier,
        data.showInland,
        data.showTotal,
        data.totalCurrency,
        data.showInvoiceInfo,
        data.invoiceCurrency
          ? data.invoiceCurrency.map((eq) => String(eq))
          : [],
        data.groupRoutes,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.averageMarginPercent,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        data.groupOriginSurcharges,
        data.groupDestinationSurcharges,
        data.groupOriginSurchargesCurrency,
        data.groupDestinationSurchargesCurrency,
        Array.isArray(data.packages)
          ? data.packages.map(PackageMapper.map)
          : [],
        data.chargeableWeight || 0,
        data.ldm,
        data.equipment,
        data.summaryCurrency,
      );
    }

    if (data.discriminator === quoteType.airtariff) {
      return new AirTariffQuote(
        data.id || 0,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.direction,
        data.incotermType,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.localCostLabels
          ? data.localCostLabels.map(LocalCostLabelMapper.map)
          : [],
        data.weightBrackets ? data.weightBrackets.map((wb) => String(wb)) : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.comment,
        data.showCarrier,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        data.hasOrigin,
        data.hasDestination,
      );
    }

    if (data.discriminator === quoteType.ltl) {
      return new LtlQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        "",
        "",
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.hasOrigin,
        data.hasDestination,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.intendedPickupDate ? new Date(data.intendedPickupDate) : null,
        data.weight,
        data.measurement,
        data.pieces || 0,
        data.comment,
        data.showRateSurcharges,
        data.showNoMarginSurcharges,
        data.showCarrier,
        data.showInland,
        data.showTotal,
        data.totalCurrency,
        data.showInvoiceInfo,
        data.invoiceCurrency
          ? data.invoiceCurrency.map((eq) => String(eq))
          : [],
        data.groupRoutes,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.averageMarginEuro,
        data.averageMarginPercent,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        data.groupOriginSurcharges,
        data.groupDestinationSurcharges,
        data.groupOriginSurchargesCurrency,
        data.groupDestinationSurchargesCurrency,
        Array.isArray(data.packages)
          ? data.packages.map(PackageMapper.map)
          : [],
        data.chargeableWeight || 0,
        data.ldm,
        Array.isArray(data.ltlQuoteSurchargeTypes)
          ? data.ltlQuoteSurchargeTypes.map(QuoteSurchargeTypeMapper.map)
          : [],
      );
    }

    if (data.discriminator === quoteType.fclTariff) {
      return new FclTariff(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.direction,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(LocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(LocationMapper.map)
          : [],
        data.equipment ? data.equipment.map((eq) => String(eq)) : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.comment,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.rates && Array.isArray(data.rates)
          ? data.rates.map(RateMapper.map)
          : [],
        <FclTier>TierMapper.map(data.tier),
        data.showRateSurcharges,
        data.showInternalNote,
        data.showExternalNote,
        data.showRateName,
        data.showAllLocationsInOneSheet,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.inquiry,
        data.source,
        data.reasonOfDecline,
        data.input,
      );
    }

    if (data.discriminator === quoteType.manual) {
      return new ManualQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.inquiry,
        data.source,
        data.htmlContent,
        data.netPrice,
        data.margin,
        data.manualQuoteType,
        data.reasonOfDecline,
        data.input,
        data.currency,
      );
    }

    if (data.discriminator === quoteType.email) {
      return new EmailQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.inquiry,
        data.source,
        data.reasonOfDecline,
        data.input,
      );
    }

    if (data.discriminator === quoteType.ftl) {
      return new FtlQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        "",
        "",
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.hasOrigin,
        data.hasDestination,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.weight,
        data.measurement,
        data.pieces || 0,
        data.comment,
        data.showRateSurcharges,
        data.showNoMarginSurcharges,
        data.showCarrier,
        data.showInland,
        data.showTotal,
        data.totalCurrency,
        data.showInvoiceInfo,
        data.invoiceCurrency
          ? data.invoiceCurrency.map((eq) => String(eq))
          : [],
        data.groupRoutes,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.averageMarginEuro,
        data.averageMarginPercent,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        Array.isArray(data.packages)
          ? data.packages.map(PackageMapper.map)
          : [],
        data.chargeableWeight || 0,
        data.ldm,
      );
    }

    if (data.discriminator === quoteType.ptl) {
      return new PtlQuote(
        data.id,
        data.displayId,
        data.groupId,
        data.ref,
        data.sender ? BaseUserMapper.map(data.sender) : null,
        data.createdBy ? BaseUserMapper.map(data.createdBy) : null,
        data.status,
        data.discriminator,
        "",
        "",
        data.clients && Array.isArray(data.clients)
          ? data.clients.map(ClientMapper.map)
          : null,
        data.hasOrigin,
        data.hasDestination,
        data.originLocations && Array.isArray(data.originLocations)
          ? data.originLocations.map(QuoteLocationMapper.map)
          : [],
        data.destinationLocations && Array.isArray(data.destinationLocations)
          ? data.destinationLocations.map(QuoteLocationMapper.map)
          : [],
        data.createdAt ? new Date(data.createdAt) : new Date(),
        data.updatedAt ? new Date(data.updatedAt) : new Date(),
        data.updatedBy ? BaseUserMapper.map(data.updatedBy) : null,
        data.openedAt ? new Date(data.openedAt) : null,
        validFrom,
        validTo,
        data.intendedPickupDate ? new Date(data.intendedPickupDate) : null,
        data.intendedPickupDateTo ? new Date(data.intendedPickupDateTo) : null,
        data.intendedDeliveryDateFrom
          ? new Date(data.intendedDeliveryDateFrom)
          : null,
        data.intendedDeliveryDateTo
          ? new Date(data.intendedDeliveryDateTo)
          : null,
        data.intendedPickupTimeFrom || null,
        data.intendedPickupTimeTo || null,
        data.intendedDeliveryTimeFrom || null,
        data.intendedDeliveryTimeTo || null,
        data.validFromTime || null,
        data.validToTime || null,
        data.weight,
        data.measurement,
        data.pieces || 0,
        data.comment,
        data.showRateSurcharges,
        data.showNoMarginSurcharges,
        data.showCarrier,
        data.showInland,
        data.showTotal,
        data.totalCurrency,
        data.showInvoiceInfo,
        data.invoiceCurrency
          ? data.invoiceCurrency.map((eq) => String(eq))
          : [],
        data.groupRoutes,
        QuoteEmailMapper.map(data.email),
        data.internalComment,
        data.customTerms,
        data.fileName,
        data.fileNameInternal,
        data.averageMargin,
        data.averageMarginEuro,
        data.averageMarginPercent,
        data.inquiry,
        data.source,
        data.insurance,
        data.reasonOfDecline,
        data.input,
        data.offerLanguage,
        Array.isArray(data.packages)
          ? data.packages.map((pkg) => PackageMapper.map(pkg, false))
          : [],
        data.chargeableWeight || 0,
        data.ldm,
        Array.isArray(data.ltlQuoteSurchargeTypes)
          ? data.ltlQuoteSurchargeTypes.map(QuoteSurchargeTypeMapper.map)
          : [],
      );
    }
  }
}
